import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import moment from 'moment';
import { Moment } from 'moment';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable, of, Subject } from 'rxjs';
import { finalize, first } from 'rxjs/operators';
import { DictionaryService } from 'src/app/data/dictionary.service';
import { DictionaryItem } from 'src/app/models/DictionaryItem';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { WorkerLegalizationActiveFileDto } from 'src/app/models/dtos/worker-legalization-active-file-dto';
import { LegalizationDocumentInformationDto } from 'src/app/models/dtos/legalization-document-information-dto';
import { LegalizationService } from 'src/app/data/legalization.service';
import { LegalizationDocumentFileTypeDto } from 'src/app/models/dtos/legalization-document-files-type-dto';
import { LegalizationStatusEnum } from 'src/app/models/enums/legalization-status-enum';
import { LegalizationFileDto } from 'src/app/models/legalization-file-dto';
import { FileService } from 'src/app/shared/services/file-service';
import { EditLegalizationDocumentRequest } from 'src/app/contracts/requests/edit-legalization-document-request';
import { LegalizationAlertStatusEnum } from 'src/app/models/enums/legalization-alert-status-enum';
import { LegalizationDocumentStatusEnum } from 'src/app/models/enums/legalization-document-status-enum';

export interface EditLegalizationDocumentModalData {
  LegalizationDocumentId: number;
  WorkerId: number;
}

@Component({
  selector: 'app-edit-legalization-document-modal',
  templateUrl: './edit-legalization-document-modal.component.html',
  styleUrl: './edit-legalization-document-modal.component.scss',
  standalone: false
})
export class EditLegalizationDocumentModalComponent implements OnInit, OnDestroy {
  dialogRef = inject<MatDialogRef<EditLegalizationDocumentModalData>>(MatDialogRef);
  data = inject<EditLegalizationDocumentModalData>(MAT_DIALOG_DATA);
  private formBuilder = inject(UntypedFormBuilder);
  private spinner = inject(NgxSpinnerService);
  protected dictionaryService = inject(DictionaryService);
  private legalizationService = inject(LegalizationService);
  private snackbar = inject(SnackBarService);
  private fileService = inject(FileService);

  public listOfLegalizationDocumentStatuses$: Observable<DictionaryItem[]> = of([]);
  public listOfLegalizationStatuses$: Observable<DictionaryItem[]> = of([]);
  public listOfWorkerLegalizationActiveFiles$: Observable<WorkerLegalizationActiveFileDto[]> = of([]);
  public listOfLegalizationAlertStatusesForForms$: Observable<DictionaryItem[]> = of([]);

  public formGroup: UntypedFormGroup;
  public hasExpirationStartDate: boolean = false;
  public hasExpirationEndDate: boolean = false;

  selectedDocument: LegalizationDocumentInformationDto | null;

  private readonly unsubscribe$ = new Subject<void>();
  private endpoint: string;

  @ViewChild('autosize') autosize: CdkTextareaAutosize;

  constructor() { }

  get legalizationDocumentTypeId() {
    return this.formGroup.get('legalizationDocumentTypeId') as UntypedFormControl;
  }
  get startDate() {
    return this.formGroup.get('startDate') as UntypedFormControl;
  }
  get endDate() {
    return this.formGroup.get('endDate') as UntypedFormControl;
  }
  get description() {
    return this.formGroup.get('description') as UntypedFormControl;
  }
  get documentCategory() {
    return this.formGroup.get('documentCategory') as UntypedFormControl;
  }
  get documentStatus() {
    return this.formGroup.get('documentStatus') as UntypedFormControl;
  }
  get comment() {
    return this.formGroup.get('comment') as UntypedFormControl;
  }
  get status() {
    return this.formGroup.get('status') as UntypedFormControl;
  }
  get documentNumber() {
    return this.formGroup.get('documentNumber') as UntypedFormControl;
  }
  get submissionDate() {
    return this.formGroup.get('submissionDate') as UntypedFormControl;
  }
  get alertStatusId() {
    return this.formGroup.get('alertStatusId') as UntypedFormControl;
  }

  get preSelectedAgreementsArray(): number[] {
    if (this.selectedDocument && this.selectedDocument.LegalizationId) {
      return [this.selectedDocument.LegalizationId];
    }
    return [];
  }

  ngOnInit(): void {
    this.createForm();
    this.listOfLegalizationDocumentStatuses$ = this.dictionaryService.getLegalizationDocumentStatuses(false);
    this.listOfLegalizationStatuses$ = this.dictionaryService.getLegalizationStatuses(false);
    this.listOfWorkerLegalizationActiveFiles$ = this.legalizationService.getWorkerLegalizationActiveFiles(this.data.WorkerId);
    this.listOfLegalizationAlertStatusesForForms$ = this.dictionaryService.getLegalizationAlertStatusesForForms();

    if (this.data.LegalizationDocumentId) {
      this.loadLegalizationDocument(this.data.LegalizationDocumentId);
    }
  }

  isFormReadonly(): boolean {
    return this.selectedDocument?.LegalizationDocumentStatusId === LegalizationDocumentStatusEnum.Corrected && this.selectedDocument?.IsCorrected;
  }

  isSaveBtnEnabled(): boolean {
    return !!this.legalizationDocumentTypeId.value && !this.isFormReadonly() && this.formGroup.valid;
  }

  isSaveAndAttachBtnEnabled(): boolean {
    return this.isSaveBtnEnabled();
  }

  public documentStartDateFilter = (date: Moment): boolean => !this.endDate.value || date.isSameOrBefore(this.endDate.value);

  public documentEndDateFilter = (date: Moment): boolean =>
    (!this.startDate.value || date.isSameOrAfter(this.startDate.value)) && date?.isSameOrAfter(moment().startOf('day'));

  onSaveBtnClick(): void {
    if (this.formGroup.invalid) {
      return;
    }

    this.onSave();
  }

  saveButtonsVisible(): boolean {
    return !this.isFormReadonly();
  }

  onSaveAndAttachBtnClick(): void {
    if (this.formGroup.invalid) {
      return;
    }

    this.spinner.show();

    const handleSuccess = () => {
      this.snackbar.openSuccessSnackBar('Legalization.SuccessfullySaved');
      this.dialogRef.close({
        isSuccess: true,
        openAssignDocuments: true,
        workerData: {
          workerId: this.selectedDocument.WorkerId,
          legalizationDocumentId: this.data.LegalizationDocumentId,
        },
      });
    };

    this.legalizationService
      .editLegalizationDocument(this.data.LegalizationDocumentId, this.createEditRequest())
      .pipe(
        first(),
        finalize(() => this.spinner.hide()),
      )
      .subscribe({
        next: () => handleSuccess(),
      });
  }

  onClose(): void {
    this.dialogRef.close(false);
  }

  onSave(): void {
    this.spinner.show();

    const handleSuccess = () => {
      this.snackbar.openSuccessSnackBar('Legalization.SuccessfullySaved');
      this.dialogRef.close({ isSuccess: true });
    };

    this.legalizationService
      .editLegalizationDocument(this.data.LegalizationDocumentId, this.createEditRequest())
      .pipe(
        first(),
        finalize(() => this.spinner.hide()),
      )
      .subscribe({
        next: () => handleSuccess(),
      });
  }

  onLegalizationTypeSelected(type: LegalizationDocumentFileTypeDto): void {
    this.legalizationDocumentTypeId.setValue(type.Id);
    this.hasExpirationStartDate = type.HasExpirationStartDate || false;
    this.hasExpirationEndDate = type.HasExpirationEndDate || false;
    this.description.setValue(type.Description);
    this.documentCategory.setValue(type.LegalizationDocumentCategoryId);
  }

  private createEditRequest(): EditLegalizationDocumentRequest {
    return {
      LegalizationDocumentTypeId: this.legalizationDocumentTypeId.value,
      StartDate: this.startDate.value,
      EndDate: this.endDate.value,
      Comment: this.comment.value,
      LegalizationDocumentCategoryId: Number(this.documentCategory.value),
      LegalizationDocumentStatusId: Number(this.documentStatus.value),
      DocumentNumber: this.documentNumber.value,
      SubmissionDate: this.submissionDate.value,
      LegalizationStatusId: this.status.value,
      LegalizationAlertStatusId: this.alertStatusId.value
    };
  }

  private createForm() {
    this.formGroup = this.formBuilder.group({
      legalizationDocumentTypeId: [null, [Validators.required]],
      description: [null],
      startDate: [null],
      endDate: [null],
      comment: [null, [Validators.maxLength(300)]],
      documentCategory: [null, [Validators.required]],
      documentStatus: [null, [Validators.required]],
      status: [LegalizationStatusEnum.InProgress, [Validators.required]],
      documentNumber: [null, [Validators.maxLength(40)]],
      submissionDate: [null],
      alertStatusId: [null, [Validators.required]],
    });
  }

  private loadLegalizationDocument(documentId: number): void {
    this.spinner.show();
    this.legalizationService
      .getLegalizationDocumentByLegalizer(documentId)
      .pipe(
        first(),
        finalize(() => this.spinner.hide()),
      )
      .subscribe((document) => {
        if (document) {
          this.endpoint = `workers/${document.WorkerId}/files`;
          this.selectedDocument = document;
          this.legalizationDocumentTypeId.setValue(document.LegalizationDocumentTypeId);

          this.startDate.setValue(document.StartDate ? new Date(document.StartDate + 'Z') : null);
          this.endDate.setValue(document.EndDate ? new Date(document.EndDate + 'Z') : null);
          this.submissionDate.setValue(document.SubmissionDate ? new Date(document.SubmissionDate + 'Z') : null);

          this.documentNumber.setValue(document.DocumentNumber);
          this.comment.setValue(document.Comment);
          this.documentCategory.setValue(document.LegalizationDocumentCategoryId);
          this.documentStatus.setValue(document.LegalizationDocumentStatusId);
          this.status.setValue(document.LegalizationStatusId);

          if (document.LegalizationAlertStatusId) {
            this.alertStatusId.setValue(document.LegalizationAlertStatusId);
          } else {
            this.alertStatusId.setValue(LegalizationAlertStatusEnum.NoAlert);
          }

          if (document.StartDate) {
            this.startDate.setValidators([Validators.required]);
          }

          if (document.EndDate) {
            this.endDate.setValidators([Validators.required]);
          }

          if (document.SubmissionDate) {
            this.submissionDate.setValidators([Validators.required]);
          }

          if (this.isFormReadonly()) {
            this.formGroup.disable();
          }

          this.formGroup.updateValueAndValidity();
        }
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  public downloadFile(id: number, name: string): void {
    this.fileService.downloadFile(id, name, this.endpoint);
  }

  public showFile(legalizationFile: LegalizationFileDto): void {
    let name = legalizationFile.Name ?? legalizationFile.OriginalName;
    this.fileService.showFile(name, legalizationFile.Id, this.endpoint, legalizationFile.FileContent);
  }
}
